import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Apple as AppleIcon,
  Shop as PlayStoreIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import brandingConfig from '../../config/brandingConfig';

/**
 * Öffentliche Landing Page für die Mobile App.
 *
 * Lädt die Store-URLs aus Firestore (appSettings/general).
 * Der QR-Code zeigt stabil auf /mobile-app – die URLs können
 * jederzeit geändert werden, ohne dass der QR-Code neu gedruckt werden muss.
 *
 * - iOS-Nutzer werden zum App Store weitergeleitet
 * - Android-Nutzer werden zum Play Store weitergeleitet
 * - Desktop-Nutzer sehen eine Landing Page mit QR-Code und Download-Buttons
 */
const MobileAppRedirect = () => {
  const [appStoreUrl, setAppStoreUrl] = useState('');
  const [playStoreUrl, setPlayStoreUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const settingsRef = doc(db, 'appSettings', 'general');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          const ios = data.appStoreUrl || '';
          const android = data.playStoreUrl || '';

          setAppStoreUrl(ios);
          setPlayStoreUrl(android);

          // Plattform-Erkennung und sofortige Weiterleitung
          const userAgent = navigator.userAgent || navigator.vendor || window.opera;

          if (/iPhone|iPad|iPod/i.test(userAgent) && ios) {
            window.location.href = ios;
            return;
          }

          if (/Android/i.test(userAgent) && android) {
            window.location.href = android;
            return;
          }
        }
      } catch (err) {
        console.error('Fehler beim Laden der App-Links:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchLinks();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || (!appStoreUrl && !playStoreUrl)) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={2} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            {brandingConfig.appName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Die App-Links wurden noch nicht konfiguriert. Bitte kontaktieren Sie uns unter{' '}
            <strong>{brandingConfig.phone}</strong>.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Desktop-Ansicht
  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'grey.50'
    }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          {/* Logo */}
          <Box sx={{ mb: 3 }}>
            <img
              src={process.env.PUBLIC_URL + brandingConfig.logos.primary}
              alt={brandingConfig.appName}
              style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>

          <Typography variant="h5" gutterBottom>
            {brandingConfig.appName} App herunterladen
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Scannen Sie den QR-Code mit Ihrem Smartphone oder wählen Sie Ihren Store.
          </Typography>

          {/* QR-Code */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <QRCodeSVG
              value={`${window.location.origin}/mobile-app`}
              size={200}
              level="H"
            />
          </Box>

          {/* Download-Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 300, mx: 'auto' }}>
            {appStoreUrl && (
              <Button
                variant="contained"
                size="large"
                startIcon={<AppleIcon />}
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
              >
                Im App Store laden
              </Button>
            )}
            {playStoreUrl && (
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayStoreIcon />}
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: '#01875f', '&:hover': { bgcolor: '#016e4f' } }}
              >
                Bei Google Play laden
              </Button>
            )}
          </Box>

          {/* Kontakt */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
            {brandingConfig.companyName} | {brandingConfig.phone}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default MobileAppRedirect;
